import React, { useMemo, useState, useEffect, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import colors from "styles/colors";
import culture_factor_pulses from "./lib/culture_factor_pulses.json";

const PRIMARY_ROW_BG = "rgba(45, 112, 226, 0.08)";
const PRIMARY_ROW_HOVER = "rgba(45, 112, 226, 0.14)";
const PRIMARY_ACCENT_BG = "rgba(45, 112, 226, 0.12)";
const PRIMARY_ACCENT_BORDER = "rgba(45, 112, 226, 0.28)";
const PRIMARY_ACCENT_BORDER_SOFT = "rgba(45, 112, 226, 0.18)";
const NEUTRAL_BORDER = "rgba(102, 109, 121, 0.18)";
const NEUTRAL_DIVIDER = "rgba(102, 109, 121, 0.12)";
const NEUTRAL_SOFT_BG = "rgba(102, 109, 121, 0.04)";

// Animations
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Layout
const PageContainer = styled.div`
  padding: 32px;
  background-color: ${colors.background.white};
  min-height: 100vh;
  animation: ${fadeInUp} 0.4s ease-out;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const LeftHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Breadcrumbs = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CrumbLink = styled.button`
  background: transparent;
  border: none;
  padding: 0;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  cursor: pointer;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: #414141;
    background-color: #f3f4f6;
  }
`;

const CrumbSeparator = styled.span`
  color: #c0c0c0;
  font-size: 12px;
`;

const CrumbCurrent = styled.span`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MainTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: ${colors.text.gray};
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.gray};
  margin: 0 0 12px 0;
`;

const SurveyCount = styled.p`
  font-size: 12px;
  color: ${colors.text.gray};
  margin: 0 0 16px 0;
`;

// Table
const TableContainer = styled.div`
  background-color: ${colors.background.white};
  border: 1px solid ${NEUTRAL_BORDER};
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 16px 40px -24px rgba(15, 23, 42, 0.28);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: linear-gradient(180deg, ${NEUTRAL_SOFT_BG} 0%, ${colors.background.white} 100%);
`;

const TableHeaderRow = styled.tr`
  border-bottom: 1px solid ${NEUTRAL_BORDER};
`;

const TableHeaderCell = styled.th`
  padding: 18px 20px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  color: ${colors.text.gray};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${NEUTRAL_DIVIDER};
  transition: background-color 0.22s ease, box-shadow 0.22s ease;
  background-color: ${(props) => (props.$selected ? PRIMARY_ROW_BG : colors.background.white)};
  box-shadow: ${(props) =>
    props.$selected ? "inset 3px 0 0 0 rgba(33, 123, 244, 0.65)" : "none"};

  &:hover {
    background-color: ${(props) => (props.$selected ? PRIMARY_ROW_HOVER : NEUTRAL_SOFT_BG)};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 18px 20px;
  vertical-align: top;
`;

const SelectCell = styled.td`
  padding: 18px;
  width: 52px;
  vertical-align: middle;
  text-align: center;
`;

const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  pointer-events: ${(props) => (props.$disabled ? "none" : "auto")};
`;

const CheckboxInput = styled.input`
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  border: 2px solid ${colors.neutral[200]};
  background: ${colors.background.white};
  transition: all 0.2s ease;
  position: relative;

  &:checked {
    background: ${colors.primary[500]};
    border-color: ${colors.primary[500]};
  }

  &:hover {
    border-color: ${PRIMARY_ACCENT_BORDER};
  }

  &:focus-visible {
    outline: 3px solid rgba(33, 123, 244, 0.25);
    outline-offset: 2px;
  }

  &::after {
    content: "";
    display: block;
    width: 5px;
    height: 10px;
    border: 2px solid ${colors.background.white};
    border-top: 0;
    border-left: 0;
    transform: rotate(45deg) translate(1px, -1px);
    opacity: 0;
    transition: opacity 0.2s ease;
    margin: auto;
  }

  &:checked::after {
    opacity: 1;
  }

  &:disabled {
    background: ${colors.neutral[200]};
    border-color: ${NEUTRAL_DIVIDER};
    cursor: not-allowed;
  }
`;

const SurveyMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SurveyTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SurveyTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.dark};
  margin: 0;
`;

const SelectedTag = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.primary[600]};
  background: ${PRIMARY_ACCENT_BG};
  border-radius: 999px;
  padding: 4px 10px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const SurveyDescription = styled.p`
  font-size: 13px;
  color: ${colors.text.gray};
  margin: 0;
  line-height: 1.5;
  max-width: 520px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const MetaBadge = styled.span`
  font-size: 12px;
  color: ${colors.primary[600]};
  background: ${PRIMARY_ACCENT_BG};
  border-radius: 12px;
  padding: 4px 10px;
  font-weight: 500;
`;

const QuestionCount = styled.div`
  font-size: 13px;
  color: ${colors.text.gray};
  font-weight: 500;
`;

const UpdatedStamp = styled.span`
  font-size: 12px;
  color: ${colors.neutral[500]};
`;

const ActionCell = styled.td`
  padding: 18px 24px 18px 20px;
  vertical-align: middle;
  text-align: right;
`;

const ActionGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ViewButton = styled.button`
  background: ${colors.background.white};
  color: ${colors.primary[600]};
  border: 1px solid ${PRIMARY_ACCENT_BORDER_SOFT};
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${colors.primary[50]};
    border-color: ${PRIMARY_ACCENT_BORDER};
    color: ${colors.primary[600]};
    transform: translateY(-1px);
  }
`;

const SelectionSummary = styled.div`
  margin: 20px 0 28px;
  padding: 18px 22px;
  border: 1px dashed ${colors.neutral[200]};
  border-radius: 16px;
  background: ${colors.background.white};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  justify-content: space-between;
`;

const SelectionInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 240px;
`;

const SelectionHeadline = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.dark};
`;

const SelectionHelper = styled.div`
  font-size: 12px;
  color: ${colors.text.gray};
`;

const SelectionChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SelectedChip = styled.span`
  background: ${colors.primary[50]};
  color: ${colors.primary[600]};
  border: 1px solid ${PRIMARY_ACCENT_BORDER_SOFT};
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
`;

const SelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ClearSelectionButton = styled.button`
  background: ${colors.background.white};
  color: ${colors.text.gray};
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${NEUTRAL_BORDER};
    color: ${colors.text.dark};
  }
`;

const SelectionError = styled.div`
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #c24141;
  padding-top: 4px;
`;

const SelectionActionBar = styled.div`
  margin-top: 24px;
  padding: 18px 22px;
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(33, 123, 244, 0.08) 0%, rgba(33, 123, 244, 0.02) 100%);
  border: 1px solid rgba(33, 123, 244, 0.2);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
`;

const CombinedSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: ${colors.primary[600]};
  font-size: 14px;
  font-weight: 500;
`;

const CombinedHint = styled.span`
  font-size: 12px;
  color: ${colors.primary[600]};
  opacity: 0.8;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

// Drawer
const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.3);
  backdrop-filter: blur(1px);
  opacity: ${props => (props.open ? 1 : 0)};
  pointer-events: ${props => (props.open ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
  z-index: 1000;
`;

const DrawerPanel = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 480px;
  max-width: calc(100vw - 32px);
  background: ${colors.background.white};
  box-shadow: -12px 0 32px rgba(15, 23, 42, 0.12);
  transform: translateX(${props => (props.open ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  z-index: 1001;
`;

const DrawerHeader = styled.div`
  padding: 24px 24px 20px 24px;
  border-bottom: 1px solid ${NEUTRAL_DIVIDER};
  background: linear-gradient(135deg, ${NEUTRAL_SOFT_BG} 0%, ${colors.background.white} 100%);
`;

const DrawerTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.text.dark};
  margin: 0 0 8px 0;
  line-height: 1.3;
`;

const DrawerSubtitle = styled.p`
  font-size: 14px;
  color: ${colors.text.gray};
  margin: 0;
  line-height: 1.5;
`;

const DrawerBody = styled.div`
  padding: 24px;
  overflow: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const DrawerSectionTitle = styled.h4`
  font-size: 13px;
  font-weight: 700;
  color: ${colors.text.dark};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::before {
    content: '';
    width: 4px;
    height: 16px;
    background: ${colors.primary[500]};
    border-radius: 2px;
  }
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const QuestionItem = styled.div`
  border: 1px solid ${NEUTRAL_BORDER};
  border-radius: 12px;
  padding: 16px 60px 16px 16px;
  background: ${colors.background.white};
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    border-color: ${PRIMARY_ACCENT_BORDER_SOFT};
    box-shadow: 0 4px 12px rgba(17, 24, 39, 0.06);
    transform: translateY(-1px);
  }
`;

const QuestionNumber = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${colors.primary[100]};
  color: ${colors.primary[600]};
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
  min-width: 24px;
  text-align: center;
`;

const QuestionText = styled.p`
  font-size: 15px;
  color: ${colors.text.dark};
  margin: 0;
  line-height: 1.5;
  font-weight: 500;
`;

const DrawerFooter = styled.div`
  padding: 20px 24px 24px 24px;
  border-top: 1px solid ${NEUTRAL_DIVIDER};
  background: ${NEUTRAL_SOFT_BG};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;

const UsePulseButton = styled.button`
  background: linear-gradient(135deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
  color: ${colors.background.white};
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(17, 24, 39, 0.15);
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    transform: none;
    box-shadow: none;
  }

  &:disabled:hover {
    transform: none;
    box-shadow: none;
  }

  &:not(:disabled):hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(17, 24, 39, 0.2);
  }
  
  &:not(:disabled):active {
    transform: translateY(0);
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 32px;
  height: 32px;
  border: none;
  background: ${NEUTRAL_SOFT_BG};
  color: ${colors.text.gray};
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${colors.neutral[200]};
    color: ${colors.text.dark};
  }
`;

const TemplateContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Add all libraries here
const LIBRARIES = [culture_factor_pulses];

const PulseLibraryPage = ({ navigateToForm }) => {
  const navigate = useNavigate();
  const [previewSurvey, setPreviewSurvey] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedSurveyIds, setSelectedSurveyIds] = useState([]);
  const [selectionError, setSelectionError] = useState("");

  const allPulses = useMemo(() => {
    const surveys = (LIBRARIES || []).flatMap((lib) => lib?.surveys || []);
    return surveys.filter((s) => s?.type === "Pulse" || typeof s?.type === "undefined");
  }, []);

  const filtered = useMemo(() => allPulses, [allPulses]);

  const selectedSurveys = useMemo(
    () => filtered.filter((s) => selectedSurveyIds.includes(s.id)),
    [filtered, selectedSurveyIds]
  );

  const handlePreview = useCallback((survey) => {
    setPreviewSurvey(survey);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setPreviewSurvey(null);
  }, []);

  const toggleSurveySelection = useCallback((survey) => {
    if (!survey?.id) return;
    setSelectedSurveyIds((prev) => {
      if (prev.includes(survey.id)) {
        setSelectionError("");
        return prev.filter((id) => id !== survey.id);
      }

      if (prev.length >= 3) {
        setSelectionError("You can select up to three surveys.");
        return prev;
      }

      setSelectionError("");
      return [...prev, survey.id];
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSurveyIds([]);
    setSelectionError("");
  }, []);

  useEffect(() => {
    if (selectedSurveyIds.length < 3 && selectionError) {
      setSelectionError("");
    }
  }, [selectedSurveyIds.length, selectionError]);

  const combinedTitle = useMemo(() => {
    if (!selectedSurveys.length) return "";
    return selectedSurveys.map((s) => s?.title || "Untitled").join(" + ");
  }, [selectedSurveys]);

  const combinedDescription = useMemo(() => {
    if (!selectedSurveys.length) return "";
    return selectedSurveys
      .map((s) => (s?.description || "").trim())
      .filter(Boolean)
      .join("\n\n");
  }, [selectedSurveys]);

  const combinedQuestions = useMemo(() => {
    if (!selectedSurveys.length) return [];
    return selectedSurveys.flatMap((survey, surveyIndex) => {
      const questions = Array.isArray(survey?.questions) ? survey.questions : [];
      return questions.map((question, questionIndex) => {
        if (typeof question === "string") {
          return {
            id: uuidv4(),
            question,
            questionFrench: "",
            aiEnhanced: false,
            task_type: "question",
            type: "text",
            scale: null,
            qualifier: null,
            options: null,
          };
        }

        return {
          ...question,
          id: question?.id
            ? `${question.id}::${surveyIndex}-${questionIndex}`
            : uuidv4(),
        };
      });
    });
  }, [selectedSurveys]);

  const resolveSurveyPayload = useCallback(() => {
    if (selectedSurveys.length > 0) {
      return {
        id: selectedSurveys.map((s) => s.id).join("+"),
        title: combinedTitle,
        description: combinedDescription,
        questions: combinedQuestions,
        sourceSurveys: selectedSurveys.map((s) => ({
          id: s.id,
          title: s.title,
        })),
      };
    }

    if (previewSurvey) {
      return previewSurvey;
    }

    return filtered[0] || null;
  }, [selectedSurveys, combinedTitle, combinedDescription, combinedQuestions, previewSurvey, filtered]);

  const handleUsePulse = useCallback(() => {
    const payload = resolveSurveyPayload();
    if (!payload) return;

    const questions = Array.isArray(payload.questions) ? payload.questions : [];

    navigateToForm({
      id: payload.id || uuidv4(),
      name: payload.title || payload.name || "Untitled Pulse",
      description: payload.description || "",
      questions,
      sourceSurveys: payload.sourceSurveys,
    });
  }, [navigateToForm, resolveSurveyPayload]);

  const isSelectionMaxed = selectedSurveyIds.length >= 3;
  const canUsePulse = selectedSurveys.length > 0 || Boolean(previewSurvey);
  const upcomingPulseName =
    selectedSurveys.length > 0
      ? combinedTitle
      : previewSurvey?.title || previewSurvey?.name || "";

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isDrawerOpen]);

  const getQuestionText = (q) => {
    if (typeof q === "string") return q;
    return q?.question || q?.text || "";
  };

  return (
    <PageContainer>
      <HeaderContainer>
        <LeftHeader>
          <Breadcrumbs>
            <CrumbLink onClick={() => navigate("/app/otp/AI/pulse-survey")}>Pulse Surveys</CrumbLink>
            <CrumbSeparator>/</CrumbSeparator>
            <CrumbCurrent>Library</CrumbCurrent>
          </Breadcrumbs>
          <TitleSection>
            <MainTitle>Pulse Library</MainTitle>
            <Subtitle>Browse pulse templates and preview questions</Subtitle>
          </TitleSection>
        </LeftHeader>
      </HeaderContainer>

      <SectionDescription>Select up to three pulse templates and blend their questions instantly.</SectionDescription>
      <SurveyCount>{filtered.length} pulses available</SurveyCount>

      <SelectionSummary>
        <SelectionInfo>
          <SelectionHeadline>{selectedSurveys.length} selected • Max 3</SelectionHeadline>
          <SelectionHelper>Multi-select templates to concatenate their question sets.</SelectionHelper>
          {selectionError ? <SelectionError>{selectionError}</SelectionError> : null}
        </SelectionInfo>
        <SelectionChipList>
          {selectedSurveys.length > 0 ? (
            selectedSurveys.map((survey) => (
              <SelectedChip key={survey.id || survey.title}>
                {survey?.title || "Untitled"}
              </SelectedChip>
            ))
          ) : (
            <SelectionHelper>No templates selected yet</SelectionHelper>
          )}
        </SelectionChipList>
        <SelectionActions>
          {selectedSurveys.length > 0 ? (
            <ClearSelectionButton type="button" onClick={clearSelection}>
              Clear selection
            </ClearSelectionButton>
          ) : null}
        </SelectionActions>
      </SelectionSummary>

      <TableContainer>
        <Table>
          <TableHeader>
            <TableHeaderRow>
              <TableHeaderCell style={{ width: "60px" }}>Select</TableHeaderCell>
              <TableHeaderCell>Survey Template</TableHeaderCell>
              <TableHeaderCell style={{ textAlign: "right" }}>Actions</TableHeaderCell>
            </TableHeaderRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <SurveyDescription style={{ maxWidth: "100%", textAlign: "center" }}>
                    No pulse templates available.
                  </SurveyDescription>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((s) => {
                const title = s?.title || "Untitled";
                const description = s?.description || "";
                const questions = Array.isArray(s?.questions) ? s.questions : [];
                const isSelected = selectedSurveyIds.includes(s.id);
                const disableCheckbox = isSelectionMaxed && !isSelected;
                const reference = s?.id ? String(s.id).slice(0, 8) : null;

                return (
                  <TableRow key={s.id || title} $selected={isSelected}>
                    <SelectCell>
                      <CheckboxLabel $disabled={disableCheckbox}>
                        <CheckboxInput
                          type="checkbox"
                          checked={isSelected}
                          disabled={disableCheckbox}
                          onChange={() => toggleSurveySelection(s)}
                          aria-label={`Select ${title}`}
                        />
                      </CheckboxLabel>
                    </SelectCell>
                    <TableCell>
                      <SurveyMain>
                        <SurveyTitleRow>
                          <SurveyTitle>{title}</SurveyTitle>
                          {isSelected ? <SelectedTag>Selected</SelectedTag> : null}
                        </SurveyTitleRow>
                        {description ? <SurveyDescription>{description}</SurveyDescription> : null}
                        <MetaRow>
                          <MetaBadge>Pulse Template</MetaBadge>
                          <QuestionCount>
                            {questions.length} {questions.length === 1 ? "question" : "questions"}
                          </QuestionCount>
                          {reference ? <UpdatedStamp>Ref • {reference}</UpdatedStamp> : null}
                        </MetaRow>
                      </SurveyMain>
                    </TableCell>
                    <ActionCell>
                      <ActionGroup>
                        <ViewButton type="button" onClick={() => handlePreview(s)}>
                          Preview
                        </ViewButton>
                      </ActionGroup>
                    </ActionCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedSurveys.length > 0 ? (
        <SelectionActionBar>
          <CombinedSummary>
            <span>{upcomingPulseName || "Combined Pulse"}</span>
            <CombinedHint>
              {combinedQuestions.length} concatenated{" "}
              {combinedQuestions.length === 1 ? "question" : "questions"}
            </CombinedHint>
          </CombinedSummary>
          <ActionButtons>
            <ClearSelectionButton type="button" onClick={clearSelection}>
              Clear selection
            </ClearSelectionButton>
            <UsePulseButton type="button" onClick={handleUsePulse} disabled={!canUsePulse}>
              Use Pulse
            </UsePulseButton>
          </ActionButtons>
        </SelectionActionBar>
      ) : null}

      {/* Drawer */}
      {isDrawerOpen && (
        <>
          <DrawerOverlay open={true} onClick={handleCloseDrawer} />
          <DrawerPanel open={true} aria-hidden={false}>
            <CloseButton aria-label="Close" onClick={handleCloseDrawer}>×</CloseButton>
            <DrawerHeader>
              <DrawerTitle>{previewSurvey?.title || previewSurvey?.name || "Untitled"}</DrawerTitle>
              {previewSurvey?.description ? (
                <DrawerSubtitle>{previewSurvey.description}</DrawerSubtitle>
              ) : null}
            </DrawerHeader>
            <DrawerBody>
              <DrawerSectionTitle>Questions</DrawerSectionTitle>
              <QuestionsList>
                {(previewSurvey?.questions || []).map((q, idx) => (
                  <QuestionItem key={q?.id || idx}>
                    <QuestionNumber>Q{idx + 1}</QuestionNumber>
                    <QuestionText>{getQuestionText(q)}</QuestionText>
                  </QuestionItem>
                ))}
              </QuestionsList>
            </DrawerBody>
            <DrawerFooter>
              <UsePulseButton type="button" onClick={handleUsePulse} disabled={!canUsePulse}>
                Use Pulse
              </UsePulseButton>
            </DrawerFooter>
          </DrawerPanel>
        </>
      )}
    </PageContainer>
  );
};

export default PulseLibraryPage;
